import { Link } from 'react-router-dom';
import { Shield, Check, FileText } from 'lucide-react';

const formatPrice = (price) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(price);

export default function InstallationCard({ product, onRequestQuote }) {
  return (
    <div
      className="bg-white rounded-xl p-6 flex flex-col relative transition-all duration-300 hover:shadow-xl border border-gray-100"
      data-testid={`install-card-${product.id}`}
    >
      {/* Badge */}
      <div className="absolute top-4 left-4 z-10">
        <span className="bg-emerald-500 text-white text-xs font-semibold uppercase px-3 py-1.5 rounded-md tracking-wider">
          Install Included
        </span>
      </div>

      {/* Product Image */}
      <Link to={`/installation/${product.slug || product.id}`} className="block bg-gray-50 rounded-xl h-64 flex items-center justify-center mt-14 mb-5 overflow-hidden" data-testid={`install-image-link-${product.id}`}>
        {(product.install_images?.length > 0 ? product.install_images[0] : product.image_url) ? (
          <img src={product.install_images?.length > 0 ? product.install_images[0] : product.image_url} alt={product.name} className="w-full h-full object-contain p-4" />
        ) : (
          <div className="text-center p-4">
            <div className="font-bold text-4xl text-emerald-700">{product.tonnage}</div>
            <div className="text-base text-gray-500 mt-2">{product.seer_rating}</div>
          </div>
        )}
      </Link>

      {/* Warranty */}
      <div className="flex items-center gap-2 mb-3">
        <Shield className="w-5 h-5 text-emerald-600" />
        <span className="text-emerald-600 text-sm font-semibold">10 Year Factory Warranty</span>
      </div>

      {/* Title */}
      <Link to={`/installation/${product.slug || product.id}`} data-testid={`install-name-link-${product.id}`}>
        <h3 className="font-bold text-gray-900 text-lg leading-snug mb-4 hover:text-emerald-700 transition-colors" data-testid={`install-name-${product.id}`}>
          {product.name}
        </h3>
      </Link>

      {/* Model */}
      <p className="text-sm text-gray-400 mb-4">Model: {product.model_number}</p>

      {/* Features */}
      <div className="mb-5 space-y-2">
        {[...product.features, '10 Year Factory Warranty', '450+ Credit Score Approved'].map((f, i) => (
          <div key={i} className={`flex items-center gap-2.5 text-sm ${f === '450+ Credit Score Approved' ? 'text-red-600 font-bold' : 'text-gray-600'}`}>
            <Check className={`w-4 h-4 flex-shrink-0 ${f === '450+ Credit Score Approved' ? 'text-red-500' : 'text-emerald-500'}`} />
            <span>{f}</span>
          </div>
        ))}
      </div>

      {/* Installed Price */}
      <div className="mt-auto pt-5 border-t border-gray-100">
        <div className="mb-4" data-testid={`install-price-${product.id}`}>
          <p className="text-xl font-bold text-gray-900">
            From <span className="text-emerald-700 text-2xl">{formatPrice(product.installed_monthly)}</span>/mo
          </p>
          <p className="text-sm font-semibold text-gray-600 mt-1">with No Payments for 6 Months! W.A.C</p>
        </div>

        <button
          onClick={() => onRequestQuote(product)}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-4 font-semibold rounded-lg transition-colors text-base flex items-center justify-center gap-2"
          data-testid={`install-quote-btn-${product.id}`}
        >
          <FileText className="w-5 h-5" />
          Schedule Installation
        </button>
      </div>
    </div>
  );
}
